import React, { useState, useRef, useEffect } from 'react';
import '../css/mediaChart.css';
import openLinkMediaIcon from '../../../../../../asset-controller-mod/asset-serv/images/openLinkMedia.png';

/* ── Types ───────────────────────────────────────────────── */

export interface MediaStatItem {
  label: string;
  value: number;
}

export interface MediaLegendItem {
  label: string;
  value: number;
  color: string;
}

export interface MediaChartProps {
  title?: string;
  totalLabel?: string;
  subLabel?: string;
  legend?: MediaLegendItem[];
  stats?: MediaStatItem[];
  footerLabel?: string;
  footerHref?: string;
  onFooterClick?: () => void;
  onMenuClick?: () => void; //NOSONAR
  onOpenClick?: () => void;
  onTitleClick?: () => void;
}

/* ── Defaults ────────────────────────────────────────────── */

const DEFAULT_LEGEND: MediaLegendItem[] = [
  { label: 'Image', value: 0, color: '#2388FF' },
];

const DEFAULT_STATS: MediaStatItem[] = [
  { label: 'In use', value: 0 },
  { label: 'Unused', value: 0 },
];

/* ── Rank-based color palette ────────────────────────────── */

const VALUE_RANK_COLORS = ['#2388FF', '#8DC1FF', '#C3DDFF', '#e3efffad'];
const BG_COLOR = '#FFFFFF';
const GAP_RAD = 0.19;

/* ── Internal enriched type ──────────────────────────────── */

interface EnrichedStat {
  label: string;
  value: number;
  color: string;
  width: string;
  startRad: number;
  endRad: number;
}

function enrichStats(stats: MediaStatItem[], total: number): EnrichedStat[] {
  const sortedUnique = Array.from(
    new Set(stats.map((s) => s.value))
  ).sort((a, b) => b - a);

  const PI2 = Math.PI * 2;
  const OFFSET = -Math.PI / 2;
  let accum = 0;

  return stats.map((stat) => {
    const startRad = OFFSET + PI2 * (accum / total);
    const endRad = OFFSET + PI2 * ((accum + stat.value) / total);
    accum += stat.value;

    /* istanbul ignore next */
    return {
      ...stat,
      color: VALUE_RANK_COLORS[sortedUnique.indexOf(stat.value)] ?? VALUE_RANK_COLORS[VALUE_RANK_COLORS.length - 1], //NOSONAR
      width: `${Math.round((stat.value / total) * 100)}%`,
      startRad: startRad + GAP_RAD / 2,
      endRad: endRad - GAP_RAD / 2,
    };
  });
}

/* ── Canvas donut renderer ───────────────────────────────── */

const CX = 103.5;
const CY = 103.5;
const RADIUS = 85;
const ARC_WIDTH = 26;
const INNER_R = RADIUS - ARC_WIDTH / 2 - 14;
 /* istanbul ignore next */
function drawEmptyDonut(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
   /* istanbul ignore next */
  const dpr  = window.devicePixelRatio || 1;
  const size = 207;
  canvas.width        = size * dpr;
  canvas.height       = size * dpr;
  canvas.style.width  = `${size}px`;
  canvas.style.height = `${size}px`;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, size, size);

  // Draw 4 equal light-blue segments with gaps between them
  const segmentColor = '#DAEAFF';
  const numSegments  = 4;
  const gapAngle     = 0.18;
  const segAngle     = (Math.PI * 2 - gapAngle * numSegments) / numSegments;

  for (let i = 0; i < numSegments; i++) {
    const startAngle = -Math.PI / 2 + i * (segAngle + gapAngle) + gapAngle / 2;
    const endAngle   = startAngle + segAngle;

    ctx.beginPath();
    ctx.arc(CX, CY, RADIUS, startAngle, endAngle);
    ctx.strokeStyle = segmentColor;
    ctx.lineWidth   = ARC_WIDTH;
    ctx.lineCap     = 'butt';
    ctx.stroke();

    // Rounded caps
    const CORNER_R = 6;
    const drawCap  = (angle: number) => {
      const x = CX + RADIUS * Math.cos(angle);
      const y = CY + RADIUS * Math.sin(angle);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.roundRect(-ARC_WIDTH / 2, -CORNER_R, ARC_WIDTH, CORNER_R * 2, CORNER_R);
      ctx.fillStyle = segmentColor;
      ctx.fill();
      ctx.restore();
    };
    drawCap(startAngle);
    drawCap(endAngle);
  }

  // Inner hole
  ctx.beginPath();
  ctx.arc(CX, CY, INNER_R, 0, Math.PI * 2);
  ctx.fillStyle = '#F8FAFF';
  ctx.fill();
}
function drawDonut(canvas: HTMLCanvasElement, enrichedStats: EnrichedStat[]) {
  const ctx = canvas.getContext('2d');
  /* istanbul ignore next */
  if (!ctx) return;
  /* istanbul ignore next */
  const dpr = window.devicePixelRatio || 1;
  const size = 207;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, size, size);

  // Background ring — full circle, no lineCap (matches inbox)
  ctx.beginPath();
  ctx.arc(CX, CY, RADIUS, 0, Math.PI * 2);
  ctx.strokeStyle = BG_COLOR;
  ctx.lineWidth = ARC_WIDTH;
  ctx.stroke();

  // Coloured segments — rounded caps sit on top of BG ring, gaps show between them
  enrichedStats.forEach((stat) => {
    ctx.beginPath();
    ctx.arc(CX, CY, RADIUS, stat.startRad, stat.endRad);
    ctx.strokeStyle = stat.color;
    ctx.lineWidth = ARC_WIDTH;
    ctx.lineCap = 'butt';
    ctx.stroke();

    // ── Rounded corner caps ──────────────────────────
    const CORNER_R = 6;

    const drawRoundedCap = (angle: number) => {
      const x = CX + RADIUS * Math.cos(angle);
      const y = CY + RADIUS * Math.sin(angle);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.roundRect(-ARC_WIDTH / 2, -CORNER_R, ARC_WIDTH, CORNER_R * 2, CORNER_R);
      ctx.fillStyle = stat.color;
      ctx.fill();
      ctx.restore();
    };

    drawRoundedCap(stat.startRad);
    drawRoundedCap(stat.endRad);
  });

  // Inner hole
  ctx.beginPath();
  ctx.arc(CX, CY, INNER_R, 0, Math.PI * 2);
  ctx.fillStyle = '#F8FAFF';
  ctx.fill();
}

/* ── Hit detection ───────────────────────────────────────── */

function getHoveredIndex(
  mouseX: number,
  mouseY: number,
  stats: EnrichedStat[],
): number | null {
  const dx = mouseX - CX;
  const dy = mouseY - CY;
  const dist = Math.sqrt(dx * dx + dy * dy); //NOSONAR

  if (dist < RADIUS - ARC_WIDTH / 2 || dist > RADIUS + ARC_WIDTH / 2) return null;

  const angle = Math.atan2(dy, dx);
  const normalise = (a: number) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const a = normalise(angle);

  for (let i = 0; i < stats.length; i++) {
    const s = normalise(stats[i].startRad);
    const e = normalise(stats[i].endRad);
    const inArc = s <= e ? (a >= s && a <= e) : (a >= s || a <= e);
    /* istanbul ignore next */
    if (inArc) return i;
  }

  return null;
}

/* ── External link icon ──────────────────────────────────── */

const ExternalLinkIcon = () => (
  <img src={openLinkMediaIcon} width="18" height="18" alt="external link" />
);

/* ── Component ───────────────────────────────────────────── */

const MediaChart: React.FC<MediaChartProps> = ({
  title = 'Media',
  totalLabel = 'Total',
  subLabel = 'Media file',
  legend = DEFAULT_LEGEND,
  stats = DEFAULT_STATS,
  footerLabel = 'Total of all Media asset library',
  footerHref = '#',
  onFooterClick,
  onOpenClick,
  onTitleClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  const total = stats.reduce((sum, s) => sum + s.value, 0);

  const allEnriched = enrichStats(stats, total || 1);
  const colorByLabel = Object.fromEntries(allEnriched.map((e) => [e.label, e.color]));

  const activeStats = stats.filter((s) => s.value > 0);
  const enrichedStats = enrichStats(activeStats, total || 1);

  // Replace the useEffect that calls drawDonut:
  useEffect(() => {
     /* istanbul ignore next */
    if (canvasRef.current) {
      if (total === 0) {
        drawEmptyDonut(canvasRef.current);  // ← new empty state renderer
      } else {
        drawDonut(canvasRef.current, enrichedStats);
      }
    }
  }, [total, hoveredIndex]);

   /* istanbul ignore next */ 
  const updateTooltip = (index: number) => {
    const stat = enrichedStats[index];
    const midAngle = (stat.startRad + stat.endRad) / 2;
    const tipR = RADIUS;
    setTooltipStyle({
      left: CX + tipR * Math.cos(midAngle),
      top: CY + tipR * Math.sin(midAngle) - 15,
      transform: 'translate(-50%, -50%)',
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    /* istanbul ignore next */
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const idx = getHoveredIndex(mouseX, mouseY, enrichedStats);
    setHoveredIndex(idx);
    /* istanbul ignore next */
    if (idx !== null) updateTooltip(idx);
  };

  const handleMouseLeave = () => setHoveredIndex(null);

  return (
    <div className="scl--cms-components-mediaChart-card">

      {/* ── Header ── */}
      <div className="scl--cms-components-mediaChart-header">
        <button
          className="scl--cms-components-mediaChart-title"
          onClick={onTitleClick}
        >
          {title}
        </button>
      </div>

      {/* ── Body ── */}
      <div className="scl--cms-components-mediaChart-body">

        {/* Canvas donut */}
        <div //NOSONAR
          ref={wrapRef}
          className="scl--cms-components-mediaChart-donutWrap"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <canvas ref={canvasRef} />

          <div className="scl--cms-components-mediaChart-donutCenter">
            <span className="scl--cms-components-mediaChart-donutLabel">{totalLabel}</span>
            <span className="scl--cms-components-mediaChart-donutValue">{total}</span>
            <span className="scl--cms-components-mediaChart-donutSub">{subLabel}</span>
          </div>

          {hoveredIndex !== null && (
            /* istanbul ignore next */
            <div
              className="scl--cms-components-mediaChart-donutTooltip"
              style={tooltipStyle}
            >
              {enrichedStats[hoveredIndex].label}: {enrichedStats[hoveredIndex].value}
              <span className="scl--cms-components-mediaChart-donutTooltipBeak" />
            </div>
          )}
        </div>

        {/* Right stats panel */}
        <div className="scl--cms-components-mediaChart-stats">

          <div className="scl--cms-components-mediaChart-legend">
            {legend.map((item) => (
              <div key={item.label} className="scl--cms-components-mediaChart-legendItem">
                <span
                  className="scl--cms-components-mediaChart-legendDot"
                  style={{ background: item.color }}
                />
                <span className="scl--cms-components-mediaChart-legendName">{item.label}</span>
                <span className="scl--cms-components-mediaChart-legendCount">{item.value}</span>
              </div>
            ))}
          </div>

          <div className="scl--cms-components-mediaChart-bars">
            {stats.map((stat) => (
              <div key={stat.label} className="scl--cms-components-mediaChart-statItem">
                <div className="scl--cms-components-mediaChart-statHeading">
                  <span className="scl--cms-components-mediaChart-statLabel">{stat.label}</span>
                  <span className="scl--cms-components-mediaChart-statValue">{stat.value}</span>
                </div>
                <div className="scl--cms-components-mediaChart-track">
                  <div
                    className="scl--cms-components-mediaChart-bar"
                    style={{
                      width: `${Math.round((stat.value / (total || 1)) * 100)}%`,
                      background: colorByLabel[stat.label] ??  /* istanbul ignore next */  '#e3efffad',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* ── Footer ── */}
      <div className="scl--cms-components-mediaChart-footer">
        <a //NOSONAR
          href={onFooterClick ? undefined : footerHref}
          className="scl--cms-components-mediaChart-footerLink"
          onClick={onFooterClick ? (e) => { e.preventDefault(); onFooterClick(); } : undefined}
          style={{ cursor: onFooterClick ? 'pointer' : undefined }}
        >
          {footerLabel}
        </a>
        <button
          className="scl--cms-components-mediaChart-openBtn"
          onClick={onOpenClick}
        >
          Open <ExternalLinkIcon />
        </button>
      </div>

    </div>
  );
};

export default MediaChart;